import { concat, encodeFunctionData, } from 'viem';
import { parseAccount } from 'viem/accounts';
import { estimateGasFeesWithReplacement } from './middleware/estimateGasFees';
import { isUsingAlchemyBundler } from '../../utils/isUsingAlchemyBundler';
import { isPaymasteredOp } from '../../utils/isPaymasteredOp';
import { estimateGasLimit } from './middleware/estimateGasLimit';
import { prepareSponsoredOrUSDCGasOperation } from './middleware/paymasters/sponsorOrUSDC';
import { ActionRejectedException, isActionRejectedException } from '../../errors';
const defaultParameters = ['factory', 'fees', 'gas', 'paymaster', 'nonce', 'signature'];
export const prepareUserOperation = ({ bundlerUrl, paymasterProxyUrl, paymasterProxyAuthSecret, rootKeyAddress, fetchAccessTokenFn, usePimlicoPaymaster, enableUsdc, }) => async (client, parameters_) => {
    const parameters = parameters_;
    const hooks = parameters.hooks ?? {};
    const { account: account_ = client.account, parameters: properties = defaultParameters,
    // stateOverride,
     } = parameters;
    if (!account_)
        throw new Error('Account not found');
    const account = parseAccount(account_);
    const bundlerClient = client;
    const rpcClient = bundlerClient.client;
    if (!rpcClient)
        throw new Error('RPC client not found');
    let request = {
        ...parameters,
        sender: account.address,
    };
    ////////////////////////////////////////////////////////////////////////////////
    // Concurrently prepare properties required to fill the User Operation.
    ////////////////////////////////////////////////////////////////////////////////
    const [callData, factory, nonce] = await Promise.all([
        (async () => {
            if (parameters.calls)
                return account.encodeCalls(parameters.calls.map((call_) => {
                    const call = call_;
                    if (call.abi)
                        return {
                            data: encodeFunctionData(call),
                            to: call.to,
                            value: call.value,
                        };
                    return call;
                }));
            return parameters.callData;
        })(),
        (async () => {
            if (!properties.includes('factory'))
                return undefined;
            if (parameters.initCode)
                return { initCode: parameters.initCode };
            if (parameters.factory && parameters.factoryData) {
                return {
                    factory: parameters.factory,
                    factoryData: parameters.factoryData,
                };
            }
            const { factory, factoryData } = await account.getFactoryArgs();
            if (account.entryPoint.version === '0.6') {
                return {
                    initCode: factory && factoryData ? concat([factory, factoryData]) : undefined,
                };
            }
            return {
                factory,
                factoryData,
            };
        })(),
        (async () => {
            if (!properties.includes('nonce'))
                return undefined;
            if (typeof parameters.nonce === 'bigint')
                return parameters.nonce;
            return account.getNonce();
        })(),
    ]);
    ////////////////////////////////////////////////////////////////////////////////
    // Fill User Operation with the prepared properties from above.
    ////////////////////////////////////////////////////////////////////////////////
    if (typeof callData !== 'undefined')
        request.callData = callData;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    if (typeof factory !== 'undefined')
        request = { ...request, ...factory };
    if (typeof nonce !== 'undefined')
        request.nonce = nonce;
    ////////////////////////////////////////////////////////////////////////////////
    // Fill User Operation with the `signature` property.
    ////////////////////////////////////////////////////////////////////////////////
    if (properties.includes('signature')) {
        if (typeof parameters.signature !== 'undefined')
            request.signature = parameters.signature;
        else
            request.signature = await account.getStubSignature(request);
    }
    ////////////////////////////////////////////////////////////////////////////////
    // `initCode` is required to be filled with EntryPoint 0.6.
    ////////////////////////////////////////////////////////////////////////////////
    // If no `initCode` is provided, we use an empty bytes string.
    if (account.entryPoint.version === '0.6' && !request.initCode)
        request.initCode = '0x';
    ////////////////////////////////////////////////////////////////////////////////
    // If we're using stackup bundler, the stackup paymaster requires gas fees
    ////////////////////////////////////////////////////////////////////////////////
    if (!isUsingAlchemyBundler(bundlerUrl) || usePimlicoPaymaster) {
        const fees = await estimateGasFeesWithReplacement({
            sender: account.address,
            client: rpcClient,
        });
        request = {
            ...request,
            ...fees,
        };
    }
    ////////////////////////////////////////////////////////////////////////////////
    // Fill User Operation with paymaster-related properties for **sending** the User Operation.
    ////////////////////////////////////////////////////////////////////////////////
    if (!request.sender)
        request.sender = '0xempty';
    if (!request.callGasLimit)
        request.callGasLimit = 0n;
    if (!request.verificationGasLimit)
        request.verificationGasLimit = 0n;
    if (!request.preVerificationGas)
        request.preVerificationGas = 0n;
    if (!request.maxFeePerGas)
        request.maxFeePerGas = 0n;
    if (!request.maxPriorityFeePerGas)
        request.maxPriorityFeePerGas = 0n;
    if (!request.nonce)
        request.nonce = 0n;
    if (!request.signature)
        request.signature = '0x';
    if (typeof callData !== 'undefined')
        request.callData = callData;
    if (typeof nonce !== 'undefined')
        request.nonce = nonce;
    if (properties.includes('paymaster')) {
        try {
            // for sponsored ops like joining a paid space, the paymaster will fail unless they have enough USDC in their account to transfer
            // so just show them the prompt to require them to add USDC to their account
            if ((await hooks?.prePaymaster?.({
                userOp: request,
            }))?.cancel) {
                throw new ActionRejectedException();
            }
            const result = await prepareSponsoredOrUSDCGasOperation({
                userOp: request,
                rpcClient,
                fetchAccessTokenFn,
                rootKeyAddress,
                bundlerUrl,
                paymasterProxyUrl,
                paymasterProxyAuthSecret,
                bundlerClient,
                enableUsdc,
                hooks,
            });
            if (result) {
                request = {
                    ...request,
                    ...result.gas,
                    callData: result.callData,
                };
            }
        }
        catch (error) {
            if (isActionRejectedException(error)) {
                throw error;
            }
            console.error('[prepareUserOperation] error', error);
        }
    }
    // 0.6 requires paymasterAndData to be provided
    if (account.entryPoint.version === '0.6') {
        if (!request.paymasterAndData) {
            request.paymasterAndData = '0x';
        }
    }
    ////////////////////////////////////////////////////////////////////////////////
    // If using Alchemy bundler and no gas fees have been set yet, set them
    ////////////////////////////////////////////////////////////////////////////////
    if (isUsingAlchemyBundler(bundlerUrl) &&
        !isPaymasteredOp({
            entryPointVersion: account.entryPoint.version,
            paymasterAndData: request.paymasterAndData,
            paymasterData: request.paymasterData,
        })) {
        const fees = await estimateGasFeesWithReplacement({
            sender: account.address,
            client: rpcClient,
        });
        request = {
            ...request,
            ...fees,
        };
    }
    ////////////////////////////////////////////////////////////////////////////////
    // Fill User Operation with gas-related properties.
    ////////////////////////////////////////////////////////////////////////////////
    if (properties.includes('gas')) {
        // If the Account has opinionated gas estimation logic, run the `estimateGas` hook and
        // fill the request with the prepared gas properties.
        if (account.userOperation?.estimateGas) {
            const gas = await account.userOperation.estimateGas(request);
            request = {
                ...request,
                ...gas,
            };
        }
        // If not all the gas properties are already populated, we will need to estimate the gas
        // to fill the gas properties.
        if (typeof request.callGasLimit === 'undefined' ||
            request.callGasLimit === 0n ||
            typeof request.preVerificationGas === 'undefined' ||
            request.preVerificationGas === 0n ||
            typeof request.verificationGasLimit === 'undefined' ||
            request.verificationGasLimit === 0n ||
            (request.paymaster && typeof request.paymasterPostOpGasLimit === 'undefined') ||
            (request.paymaster && typeof request.paymasterVerificationGasLimit === 'undefined')) {
            const gas = await estimateGasLimit({
                userOp: request,
                account,
                client: bundlerClient,
            });
            if (gas) {
                request = {
                    ...request,
                    callGasLimit: gas.callGasLimit,
                    preVerificationGas: gas.preVerificationGas,
                    verificationGasLimit: gas.verificationGasLimit,
                    paymasterPostOpGasLimit: gas.paymasterPostOpGasLimit,
                    paymasterVerificationGasLimit: gas.paymasterVerificationGasLimit,
                };
            }
        }
    }
    ////////////////////////////////////////////////////////////////////////////////
    // Remove redundant properties that do not conform to the User Operation schema.
    ////////////////////////////////////////////////////////////////////////////////
    delete request.calls;
    delete request.parameters;
    delete request.paymasterContext;
    if (typeof request.paymaster !== 'string')
        delete request.paymaster;
    if ('hooks' in request)
        delete request.hooks;
    return request;
};
